import { useState, useEffect } from "react";
import { avatarBg } from "@/lib/avatar";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "@/App";
import { Button } from "@/components/ui/button";
import { Star, Calendar, MapPin, User, RotateCcw } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const Stars = ({ rating }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((s) => (
      <Star key={s} className={`w-4 h-4 ${s <= rating ? "text-amber-400 fill-amber-400" : "text-white/15"}`} />
    ))}
  </div>
);

export const CompletedDateModal = ({ isOpen, onClose, offer, otherUser, onRebook }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [reviews, setReviews] = useState(null);

  useEffect(() => {
    if (isOpen && offer?.offer_id) {
      setReviews(null);
      api.get(`/date-reviews/offer/${offer.offer_id}`)
        .then((res) => setReviews(res.data.reviews))
        .catch(() => setReviews([]));
    }
  }, [isOpen, offer?.offer_id]);

  if (!offer) return null;

  const myReview = reviews?.find((r) => r.reviewer_id === user?.user_id);
  const theirReview = reviews?.find((r) => r.reviewer_id !== user?.user_id);

  const ReviewBlock = ({ label, review }) => (
    <div className="bg-white/5 border border-white/10 rounded-xl p-4">
      <p className="text-xs font-medium text-muted-foreground mb-2">{label}</p>
      {review ? (
        <>
          <Stars rating={review.rating} />
          {review.comment && (
            <p className="text-sm text-white/85 italic mt-2">"{review.comment}"</p>
          )}
        </>
      ) : (
        <p className="text-sm text-muted-foreground">No review yet</p>
      )}
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent data-testid="completed-date-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <img
              src={otherUser?.photos?.[0] || otherUser?.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(otherUser?.name || "?")}&background=${avatarBg()}&color=fff&size=100`}
              alt={otherUser?.name}
              className="w-10 h-10 rounded-full object-cover"
            />
            Date with {otherUser?.name}
          </DialogTitle>
          <DialogDescription>Completed date summary and reviews</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="bg-primary/10 rounded-xl p-4 text-sm space-y-1.5">
            <p className="text-xl font-bold text-primary">${offer.offer_amount}</p>
            {offer.date_details?.proposed_date && (
              <p className="flex items-center gap-2 text-white/80">
                <Calendar className="w-4 h-4" />
                {offer.date_details.proposed_date}{offer.date_details?.proposed_time ? ` · ${offer.date_details.proposed_time}` : ""}
              </p>
            )}
            {offer.date_details?.location && (
              <p className="flex items-center gap-2 text-white/80">
                <MapPin className="w-4 h-4" />
                {offer.date_details.location}
              </p>
            )}
          </div>

          {reviews === null ? (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="space-y-3" data-testid="date-reviews-section">
              <ReviewBlock label="Your review" review={myReview} />
              <ReviewBlock label={`${otherUser?.name?.split(" ")[0] || "Their"}'s review`} review={theirReview} />
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:flex-row flex-col">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => {
              onClose();
              navigate(`/profile/${otherUser?.user_id}`);
            }}
            data-testid="view-profile-btn"
          >
            <User className="w-4 h-4 mr-2" />
            View Profile
          </Button>
          <Button
            className="flex-1 gradient-gold text-black font-semibold"
            onClick={onRebook}
            data-testid="rebook-date-btn"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Rebook This Date
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CompletedDateModal;
